/*
  TaskPane — one place to read and edit a task (screen 12-tasks, idea 7). It replaces the old Task Detail
  sheet (a wall of check boxes) and the unused TaskModal:
    • docked beside the board on wide screens (an in-page column, not an overlay); a right sheet on
      smaller screens and full screen on phones;
    • editable title; Status as a segmented control — Blocked asks for a reason before it is saved,
      Cancel lives in ⋯; then read-mode rows (Priority, Due + "Make multi-day", Assignees with a searchable
      picker, Project, Section, Location, Origin) that open small pickers;
    • Notes (rich text, cleaned), Attachments (drag & drop), Comments (who wrote what, and when);
    • every change saves on its own and the head says so: "✓ Saved · just now";
    • Delete lives in ⋯ and asks first;
    • "New task" opens it empty and nothing is saved until the task has a name (no more "Untitled task").
    • Escape works one step at a time, the same docked and as a sheet: a picker / menu closes first, then a
      box being edited (notes, blocked reason, a text field), then the pane. Typed work that is not saved yet
      (a new task's draft, notes being edited, a comment, a blocked reason) is reported with onDirtyChange, and
      the page asks "Discard …?" before the pane closes or shows another task.
  Test ids kept from the sheet: task-detail-sheet, task-detail-title, task-detail-status,
  task-detail-comment, task-detail-done, task-detail-origin(-link).
  `readOnly` (BUG-002: Tasks is View only for the role in Admin › Access, or a Viewer account — the server refuses
  every write there): the same pane to READ the task. Title, status, priority, dates, people, project, section,
  location and notes are plain text; no pickers, no "Add", no notes editor, no file drop / remove, no comment box or
  comment delete, no Mark done / Reopen / Cancel / Delete — ⋯ keeps Open in project and Copy link.
*/
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Bold,
  Check,
  ChevronDown,
  CircleCheck,
  CircleSlash,
  ExternalLink,
  File as FileIcon,
  FileSpreadsheet,
  FileText,
  GitBranch,
  Image as ImageIcon,
  Italic,
  Link2,
  List,
  ListOrdered,
  Loader2,
  Package,
  Pencil,
  Plus,
  ReceiptText,
  RotateCcw,
  Trash2,
  TriangleAlert,
  Upload,
  X,
} from 'lucide-react';
import api, { openAuthenticatedFile } from '@/lib/api';
import { cn } from '@/lib/utils';
import { formatDate, formatDateTime, formatTimeAgo } from '@/lib/format';
import { getErrorMessage } from '@/lib/errors';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { RowActions } from '@/components/ui/row-actions';
import { Segmented } from '@/components/ui/segmented';
import { StatusPill } from '@/components/ui/status-pill';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { notify } from '@/components/ui/notify';
import { PersonAvatar, PriorityFlag } from './TaskCard';
import {
  AssignPanel,
  DueDatePanel,
  LocationPanel,
  MovePanel,
  PriorityPanel,
  ProjectPanel,
} from './TaskQuickPanels';
import { notesToText, sanitizeNotesHtml } from './richText';
import { autoNotesDirection } from './textDirection';
import {
  dueInfo,
  isoDate,
  NO_SECTION,
  normalizeStatus,
  sectionOf,
  taskAssignees,
  taskOrigin,
  projectLabel,
  taskProjectIds,
  taskProjects,
  workingDaysBetween,
} from './taskModel';
import { useQueuedSelection } from './useQueuedSelection';

const ORIGIN_ICONS = { stage: GitBranch, material: Package, invoicing: ReceiptText };
const ATTACH_ACCEPT = '.pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx,.csv,.txt,.dwg,.dxf,.zip';
const STATUS_SEGMENTS = [
  { key: 'Open', label: 'Open', dot: 'gray', testId: 'task-status-open' },
  { key: 'In Progress', label: 'In Progress', dot: 'blue', testId: 'task-status-in-progress' },
  { key: 'Blocked', label: 'Blocked', dot: 'red', testId: 'task-status-blocked' },
  { key: 'Done', label: 'Done', dot: 'navy', testId: 'task-status-done' },
];
const ADMIN_ROLES = new Set(['Admin', 'Super Admin']);
const OPEN_LAYERS = [
  '[role="dialog"][data-state="open"]',
  '[role="alertdialog"][data-state="open"]',
  '[role="menu"][data-state="open"]',
  '[role="listbox"][data-state="open"]',
  '[data-radix-popper-content-wrapper] > [data-state="open"]',
].join(', ');
const TEXT_FIELDS = 'input, textarea, select, [contenteditable="true"]';

/** What a new task's draft holds besides its name (compared with the empty draft it started from). */
const draftFields = (draft) => JSON.stringify({
  status: draft.status,
  blocked_reason: String(draft.blocked_reason || '').trim(),
  priority: draft.priority,
  due_date: draft.due_date || '',
  is_multi_day: Boolean(draft.is_multi_day),
  start_date: draft.start_date || '',
  section: draft.section || '',
  assignees: (draft.assignees || []).map((person) => person.id),
  projects: (draft.projects || []).map((item) => item.id),
  locations: draft.locations || [],
});

const emptyDraft = (prefill = {}) => ({
  name: '',
  status: 'Open',
  blocked_reason: '',
  priority: 'Medium',
  due_date: prefill.due_date || '',
  is_multi_day: false,
  start_date: '',
  assignees: prefill.assignee ? [prefill.assignee] : [],
  projects: prefill.project ? [prefill.project] : [],
  section: prefill.section && prefill.section !== NO_SECTION ? prefill.section : '',
  locations: [],
  notes_html: '',
  attachments: [],
  comments: [],
});

const formatBytes = (bytes) => {
  const size = Number(bytes || 0);
  if (!size) return '';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const fileLook = (name) => {
  const extension = String(name || '').split('.').pop()?.toLowerCase();
  if (extension === 'pdf') return { Icon: FileText, tone: 'red' };
  if (['xls', 'xlsx', 'csv'].includes(extension)) return { Icon: FileSpreadsheet, tone: 'green' };
  if (['png', 'jpg', 'jpeg', 'webp'].includes(extension)) return { Icon: ImageIcon, tone: 'blue' };
  if (['doc', 'docx', 'txt'].includes(extension)) return { Icon: FileText, tone: 'navy' };
  return { Icon: FileIcon, tone: 'gray' };
};

const valueButton = 'inline-flex min-h-[28px] max-w-full items-center gap-1.5 rounded-lg px-1.5 -mx-1.5 text-left text-[13.5px] font-semibold text-ink transition-colors hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
const linkButton = 'rounded text-[12px] font-semibold text-brand-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
const tagClasses = 'inline-flex h-6 max-w-full items-center gap-1 truncate rounded-md border border-line bg-surface-2 px-2 text-[12px] font-semibold text-ink-2';
const sectionLabel = 'text-[12.5px] font-bold text-ink';
const popoverClasses = 'w-[288px] rounded-xl border-line bg-surface p-0 pb-1 text-ink shadow-overlay';

/** A row value that opens a picker. */
function Picker({ trigger, children, open, onOpenChange, label, testId, align = 'start' }) {
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button type="button" className={valueButton} aria-label={label} data-testid={testId}>
          {trigger}
        </button>
      </PopoverTrigger>
      <PopoverContent align={align} sideOffset={6} collisionPadding={12} className={popoverClasses}>
        {children}
      </PopoverContent>
    </Popover>
  );
}

function SaveLine({ mode, save, task }) {
  const [, tick] = useState(0);
  useEffect(() => {
    const timer = window.setInterval(() => tick((value) => value + 1), 30_000);
    return () => window.clearInterval(timer);
  }, []);
  if (mode === 'new') {
    return <p className="text-[12px] text-ink-3">Not saved yet — it is created once it has a name.</p>;
  }
  const created = [
    task.created_at ? `Created ${formatDate(task.created_at, { year: 'auto' })}` : 'Created',
    task.created_by ? `by ${task.created_by}` : '',
  ].filter(Boolean).join(' ');
  let state = null;
  if (save.status === 'saving') {
    state = <span className="inline-flex items-center gap-1 font-semibold text-ink-2"><Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />Saving…</span>;
  } else if (save.status === 'error') {
    state = (
      <span className="inline-flex items-center gap-1 font-semibold text-status-red-ink" title={save.error}>
        <TriangleAlert className="h-3 w-3" aria-hidden="true" />Couldn’t save{save.error ? ` — ${save.error}` : ''}
      </span>
    );
  } else if (save.status === 'saved' && save.at) {
    state = (
      <span className="inline-flex items-center gap-1 font-semibold text-status-green-ink">
        <Check className="h-3 w-3" strokeWidth={3} aria-hidden="true" />Saved · {formatTimeAgo(save.at)}
      </span>
    );
  }
  return (
    <p className="flex flex-wrap items-center gap-x-1 text-[12px] text-ink-3" aria-live="polite" data-testid="task-pane-save-state">
      {state}
      {state ? <span aria-hidden="true">·</span> : null}
      <span>{created}</span>
    </p>
  );
}

/* ------------------------------------------------------------------------ notes */

/**
 * Notes of a saved task: read view → "Edit" → Save notes / Cancel (onSave). `onDirtyChange(bool)` says
 * whether the open editor holds changes that are not saved yet (the pane asks before they are thrown away).
 * New task (`onDraft`): there is nothing to save separately — every keystroke goes into the draft, so
 * "Create task" (button or Enter) always sends what is in the editor.
 */
function NotesBlock({ html, onSave, onDraft, onDirtyChange, disabled }) {
  const live = typeof onDraft === 'function';
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const editorRef = useRef(null);
  const clean = useMemo(() => sanitizeNotesHtml(html), [html]);

  useEffect(() => {
    if (!editing || !editorRef.current) return;
    editorRef.current.innerHTML = clean;
    autoNotesDirection(editorRef.current);
    editorRef.current.focus();
  }, [editing]); // eslint-disable-line react-hooks/exhaustive-deps -- load the notes once per edit
  useLayoutEffect(() => {
    if (!editing) onDirtyChange?.(false);
  }, [editing, onDirtyChange]);

  /** After every change in the editor: into the draft (new task), or "has unsaved notes" (saved task). */
  const report = () => {
    autoNotesDirection(editorRef.current);
    const current = editorRef.current?.innerHTML || '';
    if (live) onDraft(current);
    else onDirtyChange?.(sanitizeNotesHtml(current) !== clean);
  };
  const format = (command, value) => {
    editorRef.current?.focus();
    try {
      document.execCommand(command, false, value);
    } catch {
      // formatting is a convenience; typing still works
    }
    report();
  };
  const addLink = () => {
    const url = window.prompt('Link address (https://…)');
    if (url && /^(https?:\/\/|mailto:)/i.test(url.trim())) format('createLink', url.trim());
  };
  const save = async () => {
    if (live) {
      report();
      setEditing(false);
      return;
    }
    const next = sanitizeNotesHtml(editorRef.current?.innerHTML || '');
    if (next === clean) {
      setEditing(false);
      return;
    }
    setSaving(true);
    const ok = await onSave(next);
    setSaving(false);
    if (ok !== false) setEditing(false);
  };

  if (!editing) {
    if (disabled && !clean) return <p className="text-[13px] text-ink-3" data-testid="task-notes-empty">No notes.</p>;
    return clean ? (
      <div className="group relative">
        <div
          ref={(node) => { autoNotesDirection(node); }}
          dir="auto"
          className="tasks-notes text-start [unicode-bidi:plaintext] [&_p]:text-start [&_div]:text-start [&_li]:text-start [&_blockquote]:text-start [&_h1]:text-start [&_h2]:text-start [&_h3]:text-start rounded-[10px] bg-surface-2 px-3 py-2.5 text-[13px] leading-[1.55] text-ink [&_a]:font-semibold [&_a]:text-brand-ink [&_a]:underline [&_h1]:text-[15px] [&_h1]:font-bold [&_h2]:text-[14px] [&_h2]:font-bold [&_li]:my-0.5 [&_ol]:list-decimal [&_ol]:ps-5 [&_p+p]:mt-1.5 [&_ul]:list-disc [&_ul]:ps-5"
          // sanitizeNotesHtml keeps only simple formatting and safe links (see richText.js)
          dangerouslySetInnerHTML={{ __html: clean }}
          data-testid="task-notes"
        />
        {!disabled ? (
          <Button
            type="button"
            variant="outline"
            size="xs"
            onClick={() => setEditing(true)}
            className="absolute right-2 top-2 opacity-0 transition-opacity focus-visible:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100"
            data-testid="task-notes-edit"
          >
            <Pencil aria-hidden="true" /> Edit
          </Button>
        ) : null}
      </div>
    ) : (
      <button
        type="button"
        disabled={disabled}
        onClick={() => setEditing(true)}
        className="flex h-10 w-full items-center gap-2 rounded-[10px] border border-dashed border-[color:var(--line-dashed)] px-3 text-left text-[13px] font-semibold text-ink-3 transition-colors hover:bg-surface-hover hover:text-ink-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
        data-testid="task-notes-add"
      >
        <Plus className="h-4 w-4" aria-hidden="true" /> Add notes — steps, scope, what “done” means
      </button>
    );
  }

  const tool = 'grid h-7 w-7 place-items-center rounded-md text-ink-2 hover:bg-surface-hover hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
  return (
    <div className="overflow-hidden rounded-[10px] border border-[color:var(--brand-soft-border)] bg-surface shadow-focus">
      <div className="flex items-center gap-0.5 border-b border-line-2 bg-surface-2 px-1.5 py-1" role="toolbar" aria-label="Formatting">
        <button type="button" className={tool} onMouseDown={(event) => event.preventDefault()} onClick={() => format('bold')} aria-label="Bold" title="Bold (Ctrl+B)"><Bold className="h-3.5 w-3.5" aria-hidden="true" /></button>
        <button type="button" className={tool} onMouseDown={(event) => event.preventDefault()} onClick={() => format('italic')} aria-label="Italic" title="Italic (Ctrl+I)"><Italic className="h-3.5 w-3.5" aria-hidden="true" /></button>
        <button type="button" className={tool} onMouseDown={(event) => event.preventDefault()} onClick={() => format('insertUnorderedList')} aria-label="Bulleted list" title="Bulleted list"><List className="h-3.5 w-3.5" aria-hidden="true" /></button>
        <button type="button" className={tool} onMouseDown={(event) => event.preventDefault()} onClick={() => format('insertOrderedList')} aria-label="Numbered list" title="Numbered list"><ListOrdered className="h-3.5 w-3.5" aria-hidden="true" /></button>
        <button type="button" className={tool} onMouseDown={(event) => event.preventDefault()} onClick={addLink} aria-label="Add link" title="Add link"><Link2 className="h-3.5 w-3.5" aria-hidden="true" /></button>
      </div>
      <div
        ref={editorRef}
        dir="auto"
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-label="Task notes"
        onInput={report}
        onKeyDown={(event) => {
          // Esc only leaves the editor (it never reaches the pane or the sheet — see the pane's Escape rules).
          if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            if (live) report();
            setEditing(false);
          }
          if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
            event.preventDefault();
            save();
          }
        }}
        className="min-h-[110px] text-start [unicode-bidi:plaintext] [&_p]:text-start [&_div]:text-start [&_li]:text-start [&_blockquote]:text-start [&_h1]:text-start [&_h2]:text-start [&_h3]:text-start px-3 py-2.5 text-[13px] leading-[1.55] text-ink outline-none [&_a]:text-brand-ink [&_a]:underline [&_ol]:list-decimal [&_ol]:ps-5 [&_ul]:list-disc [&_ul]:ps-5"
        data-testid="task-notes-editor"
      />
      <div className="flex items-center gap-2 border-t border-line-2 px-2 py-1.5">
        {live ? (
          <>
            <span className="text-[11.5px] text-ink-3">Saved with the task when you create it</span>
            <span className="flex-1" />
            <Button type="button" variant="ghost" size="xs" onClick={save} data-testid="task-notes-done">Done</Button>
          </>
        ) : (
          <>
            <span className="text-[11.5px] text-ink-3">Ctrl+Enter saves · Esc cancels</span>
            <span className="flex-1" />
            <Button type="button" variant="ghost" size="xs" onClick={() => setEditing(false)} disabled={saving}>Cancel</Button>
            <Button type="button" size="xs" onClick={save} disabled={saving} data-testid="task-notes-save">
              {saving ? <Loader2 className="animate-spin" aria-hidden="true" /> : null} Save notes
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------ attachments */

function Attachments({ items, pending, uploading, onFiles, onRemove, onRemovePending, disabled }) {
  const inputRef = useRef(null);
  const [over, setOver] = useState(false);
  const count = items.length + pending.length;
  return (
    <section className="flex flex-col gap-1.5" aria-label="Attachments">
      <div className={sectionLabel}>Attachments <span className="font-semibold text-ink-3">· {count}</span></div>
      {disabled && !count ? <p className="text-[13px] text-ink-3">No files attached.</p> : null}
      {items.map((attachment) => {
        const { Icon, tone } = fileLook(attachment.name);
        return (
          <div key={attachment.id} className="group flex min-h-[40px] items-center gap-2.5 rounded-lg px-1 hover:bg-surface-hover">
            <span className={cn('ui-icon-tile h-7 w-7 rounded-lg', `tone-${tone}`)} aria-hidden="true"><Icon className="h-[15px] w-[15px]" /></span>
            <button
              type="button"
              onClick={() => openAuthenticatedFile(attachment.file_url).catch((error) => notify.error('Could not open the file', { description: getErrorMessage(error) }))}
              className="min-w-0 flex-1 truncate text-left text-[13px] font-semibold text-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              title={`Open ${attachment.name}`}
            >
              {attachment.name}
            </button>
            <span className="shrink-0 text-[12px] text-ink-3">{formatBytes(attachment.size)}</span>
            {!disabled ? (
              <button
                type="button"
                onClick={() => onRemove(attachment)}
                className="grid h-6 w-6 shrink-0 place-items-center rounded-md text-ink-3 opacity-0 transition-opacity hover:bg-status-red-bg hover:text-status-red-ink focus-visible:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100"
                aria-label={`Remove ${attachment.name}`}
                title="Remove"
              >
                <X className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            ) : null}
          </div>
        );
      })}
      {pending.map((file, index) => {
        const { Icon, tone } = fileLook(file.name);
        return (
          <div key={`${file.name}-${file.size}-${index}`} className="flex min-h-[40px] items-center gap-2.5 rounded-lg px-1">
            <span className={cn('ui-icon-tile h-7 w-7 rounded-lg', `tone-${tone}`)} aria-hidden="true"><Icon className="h-[15px] w-[15px]" /></span>
            <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-ink-2">{file.name}</span>
            {uploading ? (
              <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-ink-3" aria-label="Uploading" />
            ) : (
              <>
                <span className="shrink-0 text-[12px] text-ink-3">{formatBytes(file.size)} · on create</span>
                <button type="button" onClick={() => onRemovePending(file)} className="grid h-6 w-6 shrink-0 place-items-center rounded-md text-ink-3 hover:bg-surface-hover" aria-label={`Remove ${file.name}`}>
                  <X className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              </>
            )}
          </div>
        );
      })}
      {!disabled ? (
        <div
          onDragOver={(event) => {
            if (!Array.from(event.dataTransfer?.types || []).includes('Files')) return;
            event.preventDefault();
            setOver(true);
          }}
          onDragLeave={() => setOver(false)}
          onDrop={(event) => {
            event.preventDefault();
            setOver(false);
            if (event.dataTransfer?.files?.length) onFiles([...event.dataTransfer.files]);
          }}
          className={cn(
            'flex h-10 items-center justify-center gap-2 rounded-[10px] border border-dashed text-[12.5px] font-semibold text-ink-3 transition-colors',
            over ? 'border-[color:var(--brand-ink)] bg-brand-soft text-brand-ink' : 'border-[color:var(--line-dashed)]'
          )}
          data-testid="task-attachments-drop"
        >
          <Upload className="h-[15px] w-[15px]" aria-hidden="true" />
          {over ? 'Drop to attach' : (
            <span>
              Drop files or{' '}
              <button type="button" className={linkButton} onClick={() => inputRef.current?.click()}>browse</button>
            </span>
          )}
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={ATTACH_ACCEPT}
            className="hidden"
            data-testid="task-attachments-input"
            onChange={(event) => {
              if (event.target.files?.length) onFiles([...event.target.files]);
              event.target.value = '';
            }}
          />
        </div>
      ) : null}
    </section>
  );
}

/* ------------------------------------------------------------------------ comments */

function Comments({ task, user, onPost, onDelete, onDirtyChange, readOnly = false }) {
  const [draft, setDraft] = useState('');
  const [posting, setPosting] = useState(false);
  useEffect(() => { setDraft(''); }, [task.id]);
  const typed = Boolean(draft.trim());
  // A layout effect: the page knows about the typed comment in the same commit, before any next key press.
  useLayoutEffect(() => { onDirtyChange?.(typed); }, [onDirtyChange, typed]);
  const comments = task.comments || [];
  const legacy = String(task.comment || '').trim();
  const userId = String(user?.id || user?.sub || '');
  const isAdmin = ADMIN_ROLES.has(user?.role);
  const post = async () => {
    const text = draft.trim();
    if (!text || posting) return;
    setPosting(true);
    const ok = await onPost(text);
    setPosting(false);
    if (ok) setDraft('');
  };
  return (
    <section className="flex flex-col gap-2" aria-label="Comments">
      <div className={sectionLabel}>Comments <span className="font-semibold text-ink-3">· {comments.length + (legacy ? 1 : 0)}</span></div>
      {legacy ? (
        <div className="flex gap-2.5" data-testid="task-legacy-comment">
          <span className="mt-0.5 grid h-[22px] w-[22px] shrink-0 place-items-center rounded-full bg-[color:var(--status-gray-bg)] text-[10px] font-bold text-ink-3" aria-hidden="true">·</span>
          <div className="min-w-0 flex-1">
            <div className="text-[12px] text-ink-3"><b className="font-semibold text-ink-2">Earlier comment</b> · author not recorded</div>
            <p className="whitespace-pre-wrap break-words text-[13px] text-ink">{legacy}</p>
          </div>
        </div>
      ) : null}
      {comments.map((comment) => {
        const canDelete = !readOnly && (isAdmin || (userId && comment.author_id === userId));
        return (
          <div key={comment.id} className="group flex gap-2.5" data-testid={`task-comment-${comment.id}`}>
            <PersonAvatar person={{ id: comment.author_id, name: comment.author_name }} className="mt-0.5" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1 text-[12px] text-ink-3">
                <b className="font-semibold text-ink-2">{comment.author_name || 'Someone'}</b>
                <span>· {formatDateTime(comment.created_at, { relativeDay: true })}</span>
                {canDelete ? (
                  <RowActions
                    label="Comment actions"
                    className="ml-auto h-6 w-6 opacity-0 focus-visible:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100"
                    items={[{ label: 'Delete comment…', icon: Trash2, danger: true, onSelect: () => onDelete(comment) }]}
                  />
                ) : null}
              </div>
              <p className="whitespace-pre-wrap break-words text-[13px] text-ink">{comment.text}</p>
            </div>
          </div>
        );
      })}
      {readOnly && !comments.length && !legacy ? <p className="text-[13px] text-ink-3">No comments yet.</p> : null}
      {readOnly ? null : (
        <div className="flex flex-col gap-1.5">
          <textarea
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
                event.preventDefault();
                post();
              }
            }}
            rows={2}
            placeholder="Write a comment…"
            aria-label="Write a comment"
            className="min-h-[60px] w-full resize-y rounded-[10px] border border-line bg-surface px-3 py-2 text-[13px] text-ink outline-none placeholder:text-ink-4 focus:border-[color:var(--brand-soft-border)] focus:shadow-focus"
            data-testid="task-detail-comment"
          />
          {draft.trim() ? (
            <div className="flex items-center gap-2">
              <span className="text-[11.5px] text-ink-3">Ctrl+Enter to send</span>
              <span className="flex-1" />
              <Button type="button" size="xs" onClick={post} disabled={posting} data-testid="task-comment-submit">
                {posting ? <Loader2 className="animate-spin" aria-hidden="true" /> : null} Comment
              </Button>
            </div>
          ) : null}
        </div>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------------ pane */

export default function TaskPane({
  task,
  isNew = false,
  prefill,
  docked = true,
  members = [],
  projects = [],
  options = { sections: [], locations: [] },
  projectNameById = {},
  user,
  onClose,
  onPatch,
  onMove,
  onDone,
  onReopen,
  onDelete,
  onCreate,
  onTaskChanged,
  onOptionsChanged,
  onMembersChanged,
  onDirtyChange,
  nextUp,
  readOnly = false,
}) {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const mode = isNew ? 'new' : 'task';
  const [form, setForm] = useState(() => emptyDraft(prefill));
  const view = mode === 'new' ? form : task;
  const taskKey = mode === 'new' ? 'new' : task?.id;
  const [save, setSave] = useState({ status: 'idle', at: null, error: '' });
  const [name, setName] = useState(view?.name || '');
  const [blockedDraft, setBlockedDraft] = useState(null); // string while asking "why blocked?"
  const [openPicker, setOpenPicker] = useState(null);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [notesDirty, setNotesDirty] = useState(false);
  const [commentDirty, setCommentDirty] = useState(false);
  const rootRef = useRef(null);
  const titleRef = useRef(null);
  const skipTitleCommitRef = useRef(false);
  const sheetEscapeRef = useRef(null);

  /*
    Typed work that is not saved yet — the page asks "Discard …?" before the pane closes or shows another
    task (PendingTasksBoard → useUnsavedChanges). Everything else in the pane saves on its own.
  */
  const draftChanged = useMemo(
    () => mode === 'new' && (draftFields(form) !== draftFields(emptyDraft(prefill)) || Boolean(sanitizeNotesHtml(form.notes_html))),
    [form, mode, prefill]
  );
  const blockedTyped = blockedDraft !== null
    && Boolean(String(blockedDraft).trim())
    && String(blockedDraft).trim() !== String(view?.blocked_reason || '').trim();
  let unsaved = '';
  if (mode === 'new' && (name.trim() || draftChanged || pendingFiles.length)) unsaved = 'this new task';
  else if (notesDirty) unsaved = 'your notes';
  else if (commentDirty) unsaved = 'your comment';
  else if (blockedTyped) unsaved = 'the blocked reason';
  // Layout effects, not effects: a passive effect can run after the next key press (seen live: type a comment,
  // Esc, Esc → the pane closed before the page knew about the comment). These run in the same commit.
  useLayoutEffect(() => { onDirtyChange?.(unsaved); }, [onDirtyChange, unsaved]);
  useLayoutEffect(() => () => onDirtyChange?.(''), [onDirtyChange]);

  // A new task id (or a fresh "New task") starts clean.
  useEffect(() => {
    setSave({ status: 'idle', at: null, error: '' });
    setBlockedDraft(null);
    setOpenPicker(null);
    setPendingFiles([]);
    if (mode === 'new') setForm(emptyDraft(prefill));
  }, [taskKey]); // eslint-disable-line react-hooks/exhaustive-deps -- reset only when the task changes
  useEffect(() => {
    if (document.activeElement !== titleRef.current) setName(view?.name || '');
  }, [taskKey, view?.name]);
  useEffect(() => {
    if (mode === 'new') window.setTimeout(() => titleRef.current?.focus(), 30);
  }, [taskKey, mode]);
  // The title grows with its text (a textarea, so long names wrap instead of scrolling sideways).
  useLayoutEffect(() => {
    const element = titleRef.current;
    if (!element) return;
    element.style.height = 'auto';
    element.style.height = `${element.scrollHeight}px`;
  }, [name, docked]);

  /** Move to another section (PATCH /move puts it at the end of that lane) with the visible save state. */
  const moveSection = async (next) => {
    setSave((current) => ({ ...current, status: 'saving', error: '' }));
    const ok = await onMove(task, next);
    setSave(ok === false ? { status: 'error', at: null, error: 'Try again.' } : { status: 'saved', at: new Date().toISOString(), error: '' });
  };

  /** Save a change: in a new task it only fills the form; otherwise PATCH with the visible save state. */
  const change = useCallback(async (payload, { message } = {}) => {
    if (mode === 'new') {
      setForm((current) => ({ ...current, ...payload }));
      return true;
    }
    setSave((current) => ({ ...current, status: 'saving', error: '' }));
    try {
      const updated = await onPatch(task, payload, { message, throwOnError: true });
      setSave({ status: 'saved', at: new Date().toISOString(), error: '' });
      return updated || true;
    } catch (error) {
      setSave({ status: 'error', at: null, error: getErrorMessage(error, 'Try again.') });
      return false;
    }
  }, [mode, onPatch, task]);

  const status = normalizeStatus(view?.status);
  const assignees = mode === 'new' ? form.assignees : taskAssignees(task);
  const taskProjectsList = mode === 'new' ? form.projects : taskProjects(task, projectNameById);
  const knownPeople = useMemo(() => new Map([...members, ...assignees].map((person) => [person.id, person])), [assignees, members]);
  const knownProjects = useMemo(
    // Full project metadata supplies system_type; task summaries remain a fallback for older links.
    () => new Map([...taskProjectsList, ...projects].map((item) => [item.id, item])),
    [projects, taskProjectsList]
  );

  const [assigneeIds, toggleAssigneeId] = useQueuedSelection(taskKey, assignees.map((person) => person.id), (nextIds) => change({
    assignee_ids: nextIds,
    assignees: nextIds.map((id) => knownPeople.get(id)).filter(Boolean),
  }));
  const [projectIds, toggleProjectId] = useQueuedSelection(taskKey, taskProjectsList.map((item) => item.id), (nextIds) => change({
    project_ids: nextIds,
    projects: nextIds.map((id) => knownProjects.get(id)).filter(Boolean).map((item) => ({ id: item.id, name: item.name })),
  }));
  const [locations, toggleLocation] = useQueuedSelection(taskKey, view?.locations || [], (next) => change({ locations: next }));
  const shownAssignees = assigneeIds.map((id) => knownPeople.get(id)).filter(Boolean);
  const shownProjects = projectIds.map((id) => knownProjects.get(id)).filter(Boolean);

  /*
    Escape — the same in the docked pane and in the sheet, one step at a time:
      • in a picker, menu or dialog: Radix closes that layer and marks the key handled (defaultPrevented) —
        only that layer closes, never the pane (also when React has already re-rendered it as closed);
      • in the notes editor or the "Why is it blocked?" box: that box closes (its own handler stops the key);
      • in another text field (title, comment): the field is left (blur) — typed text stays;
      • otherwise the pane closes — through onClose, which asks first while something is unsaved.
    The sheet (a Radix dialog) would close itself on Escape before any of this runs, so it hands every Escape
    to this listener instead (onEscapeKeyDown below marks it as ours).
  */
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      const fromSheet = sheetEscapeRef.current === event;
      sheetEscapeRef.current = null;
      if (event.defaultPrevented && !fromSheet) return;
      const root = rootRef.current;
      const otherLayerOpen = [...document.querySelectorAll(OPEN_LAYERS)].some((layer) => !root || !layer.contains(root));
      if (otherLayerOpen) return;
      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest(TEXT_FIELDS)) {
        // Leave the field but stay in the pane (the next Esc closes it; Tab goes on from here).
        if (root?.contains(target)) root.focus({ preventScroll: true });
        return;
      }
      if (!docked && !fromSheet) return;
      onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [docked, onClose]);

  // Docked: opening a task moves the focus into the pane (it sits after the board in the page), and closing
  // it gives the focus back to what opened it (a card, a table row…). The sheet does both itself.
  useEffect(() => {
    if (!docked) return undefined;
    const root = rootRef.current;
    const opener = document.activeElement instanceof HTMLElement && !root?.contains(document.activeElement)
      ? document.activeElement
      : null;
    if (mode === 'task') root?.focus({ preventScroll: true });
    return () => {
      const active = document.activeElement;
      const focusLost = !active || active === document.body || root?.contains(active);
      if (focusLost && opener?.isConnected) opener.focus({ preventScroll: true });
    };
  }, [docked, mode]);

  // Docked: toasts (Done · Undo, "assigned to …") must not cover the pane's fields. While the pane is open it
  // publishes how far from the right edge toasts should stay — `--toast-right-offset` on <html>, the same way the
  // UnsavedBar publishes --toast-bottom-offset — so they sit under the board, just left of the pane.
  useEffect(() => {
    if (!docked) return undefined;
    const html = document.documentElement;
    const update = () => {
      const pane = rootRef.current;
      if (!pane) return;
      // The Toaster keeps 24px from the right edge already; 16px of air between a toast and the pane.
      const needed = window.innerWidth - pane.getBoundingClientRect().left - 24 + 16;
      if (needed > 0) html.style.setProperty('--toast-right-offset', `${Math.ceil(needed)}px`);
      else html.style.removeProperty('--toast-right-offset');
    };
    update();
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('resize', update);
      html.style.removeProperty('--toast-right-offset');
    };
  }, [docked]);

  const addMember = async (personName) => {
    try {
      const response = await api.post('/tasks/team-members', { name: personName, role: 'Team Member' });
      onMembersChanged?.(response.data);
      await toggleAssigneeId(response.data.id);
    } catch (error) {
      notify.error('Could not add the person', { description: getErrorMessage(error) });
    }
  };
  const addOption = async (type, value) => {
    try {
      const response = await api.post('/tasks/options', { option_type: type, value });
      onOptionsChanged?.(response.data);
      return value;
    } catch (error) {
      notify.error(`Could not add the ${type}`, { description: getErrorMessage(error) });
      return '';
    }
  };

  const commitName = async () => {
    if (skipTitleCommitRef.current) {
      skipTitleCommitRef.current = false; // Esc put the saved name back
      return;
    }
    const next = name.trim();
    if (mode === 'new') {
      setForm((current) => ({ ...current, name: next }));
      return;
    }
    if (!next) {
      setName(task.name || '');
      return;
    }
    if (next !== task.name) await change({ name: next });
  };

  const create = async () => {
    const taskName = name.trim();
    if (!taskName || creating) {
      if (!taskName) titleRef.current?.focus();
      return;
    }
    if (form.status === 'Blocked' && !String(form.blocked_reason || '').trim()) {
      setBlockedDraft('');
      return;
    }
    setCreating(true);
    // The notes editor writes into the draft on every keystroke, so this is what is on screen right now.
    const notes = sanitizeNotesHtml(form.notes_html);
    const payload = {
      name: taskName,
      status: form.status,
      blocked_reason: form.status === 'Blocked' ? form.blocked_reason : '',
      priority: form.priority,
      due_date: form.due_date || '',
      is_multi_day: Boolean(form.is_multi_day),
      start_date: form.is_multi_day ? form.start_date : '',
      assignee_ids: form.assignees.map((person) => person.id),
      assignees: form.assignees,
      project_ids: form.projects.map((item) => item.id),
      projects: form.projects,
      section: form.section || '',
      locations: form.locations,
      notes_html: notes,
      description: notesToText(notes).slice(0, 500),
    };
    const created = await onCreate(payload);
    if (created && pendingFiles.length) await upload(pendingFiles, created);
    setCreating(false);
  };

  const upload = async (files, target = task) => {
    if (!target) return;
    setUploading(true);
    let current = target;
    const failed = [];
    for (const file of files) {
      const data = new FormData();
      data.append('file', file);
      try {
        // eslint-disable-next-line no-await-in-loop -- one after the other, so each one is reported
        const response = await api.post(`/tasks/${target.id}/attachments`, data, { headers: { 'Content-Type': 'multipart/form-data' } });
        current = { ...current, attachments: [...(current.attachments || []), response.data] };
        onTaskChanged?.(current);
      } catch (error) {
        failed.push(`${file.name}: ${getErrorMessage(error)}`);
      }
    }
    setUploading(false);
    setPendingFiles([]);
    if (failed.length) notify.error(`Could not attach ${failed.length === 1 ? 'a file' : `${failed.length} files`}`, { description: failed.join(' · ') });
    else setSave({ status: 'saved', at: new Date().toISOString(), error: '' });
  };

  const removeAttachment = async (attachment) => {
    try {
      await api.delete(`/tasks/${task.id}/attachments/${attachment.id}`);
      onTaskChanged?.({ ...task, attachments: (task.attachments || []).filter((item) => item.id !== attachment.id) });
      notify.success(`“${attachment.name}” removed`);
    } catch (error) {
      notify.error('Could not remove the file', { description: getErrorMessage(error) });
    }
  };

  const postComment = async (text) => {
    try {
      const response = await api.post(`/tasks/${task.id}/comments`, { text });
      onTaskChanged?.(response.data);
      setSave({ status: 'saved', at: new Date().toISOString(), error: '' });
      return true;
    } catch (error) {
      notify.error('Could not post the comment', { description: getErrorMessage(error) });
      return false;
    }
  };
  const deleteComment = async (comment) => {
    const ok = await confirm({
      title: 'Delete this comment?',
      description: `“${String(comment.text).slice(0, 120)}${String(comment.text).length > 120 ? '…' : ''}”`,
      tone: 'danger',
      confirmLabel: 'Delete comment',
      onConfirm: async () => {
        const response = await api.delete(`/tasks/${task.id}/comments/${comment.id}`);
        onTaskChanged?.(response.data);
      },
    });
    if (ok) notify.success('Comment deleted');
  };

  const pickStatus = (next) => {
    if (next === status) return;
    if (next === 'Blocked') {
      setBlockedDraft(view?.blocked_reason || '');
      return;
    }
    setBlockedDraft(null);
    if (next === 'Done' && mode === 'task') {
      onDone(task);
      return;
    }
    change({ status: next }, { message: mode === 'task' ? `“${task.name}” is ${next === 'In Progress' ? 'in progress' : 'open'}` : undefined });
  };
  const confirmBlocked = async () => {
    const reason = String(blockedDraft || '').trim();
    if (!reason) return;
    const ok = await change({ status: 'Blocked', blocked_reason: reason }, { message: mode === 'task' ? `“${task.name}” is blocked` : undefined });
    if (ok) setBlockedDraft(null);
  };

  if (!view) return null;

  const due = isoDate(view.due_date);
  const start = view.is_multi_day ? isoDate(view.start_date) : '';
  const dueMeta = dueInfo(view);
  const origin = mode === 'task' ? taskOrigin(task) : null;
  const OriginIcon = origin ? ORIGIN_ICONS[origin.kind] : null;
  // A task from a stage / the material list / an invoice keeps its project (its link points into it).
  const homeProjectId = origin ? (taskProjectIds(task)[0] || '') : '';
  const section = view.section ? String(view.section) : '';
  const crumb = mode === 'new'
    ? 'New task'
    : [sectionOf(task), projectLabel(shownProjects[0])].filter(Boolean).join(' · ');
  const isClosed = status === 'Done' || status === 'Cancelled';
  const dueLabel = (value) => {
    const info = dueInfo({ due_date: value });
    const date = formatDate(value);
    if (info.kind === 'today') return `Today · ${date}`;
    if (info.kind === 'tomorrow') return `Tomorrow · ${date}`;
    return date;
  };

  const menuItems = mode === 'new' ? [] : [
    origin?.href ? { label: 'Open in project', icon: ExternalLink, onSelect: () => navigate(origin.href) } : null,
    { label: 'Copy link', icon: Link2, onSelect: () => copyLink(task) },
    readOnly ? null : status === 'Cancelled' || status === 'Done'
      ? { label: 'Reopen task', icon: RotateCcw, onSelect: () => onReopen(task) }
      : { label: 'Cancel task', icon: CircleSlash, onSelect: () => change({ status: 'Cancelled' }, { message: `“${task.name}” cancelled` }), testId: 'task-pane-cancel' },
    readOnly ? null : { label: 'Delete task…', icon: Trash2, danger: true, onSelect: () => onDelete(task), testId: 'task-pane-delete' },
  ];
  // Read-only (BUG-002): a row value is plain text in the place where an editor gets its picker button.
  const readValue = (content, testId) => (
    <span className="inline-flex min-h-[28px] max-w-full items-center gap-1.5 text-[13.5px] font-semibold text-ink" data-testid={testId}>{content}</span>
  );
  const dueText = due ? (
    <span className={cn(dueMeta.kind === 'overdue' && !isClosed && 'text-status-red-ink', dueMeta.kind === 'today' && !isClosed && 'text-status-amber-ink')}>
      {dueMeta.kind === 'overdue' && !isClosed ? <TriangleAlert className="mr-1 inline h-3.5 w-3.5 align-[-2px]" aria-hidden="true" /> : null}
      {dueMeta.kind === 'overdue' && !isClosed ? `Overdue · ${formatDate(due)}` : dueLabel(due)}
    </span>
  ) : <span className="font-medium text-ink-3">No date</span>;

  const content = (
    <>
      <header className="flex flex-col gap-1.5 border-b border-line-2 px-[18px] pb-3.5 pt-3">
        <div className="flex min-h-[30px] items-center gap-1">
          <span className="min-w-0 flex-1 truncate text-[12px] font-semibold text-ink-3">{crumb || NO_SECTION}</span>
          {mode === 'task' ? (
            <>
              <Button type="button" variant="ghost" size="icon-sm" className="text-ink-3" onClick={() => copyLink(task)} aria-label="Copy link to this task" title="Copy link">
                <Link2 aria-hidden="true" />
              </Button>
              <RowActions label={`More actions for ${task.name}`} items={menuItems} className="h-[30px] w-[30px]" data-testid="task-pane-menu" />
            </>
          ) : (
            <Button type="button" size="xs" onClick={create} disabled={!name.trim() || creating} className="mr-1" data-testid="task-create-submit-top">
              {creating ? <Loader2 className="animate-spin" aria-hidden="true" /> : <Plus aria-hidden="true" />} Create
            </Button>
          )}
          <Button type="button" variant="ghost" size="icon-sm" className="text-ink-3" onClick={onClose} aria-label="Close task" title="Close (Esc)" data-testid="task-pane-close">
            <X aria-hidden="true" />
          </Button>
        </div>
        {readOnly ? (
          <h2 dir="auto" className="break-words text-start font-heading text-[19px] font-extrabold leading-[1.25] tracking-[-0.01em] text-ink" data-testid="task-detail-title">
            {view.name}
          </h2>
        ) : (
          <textarea
            ref={titleRef}
            dir="auto"
            value={name}
            rows={1}
            onChange={(event) => setName(event.target.value.replace(/\n/g, ' '))}
            onBlur={commitName}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                if (mode === 'new') create();
                else event.currentTarget.blur();
              }
              if (event.key === 'Escape' && mode === 'task') {
                // Esc in the title: put the saved name back and leave the field (the pane stays open).
                event.preventDefault();
                event.stopPropagation();
                skipTitleCommitRef.current = true;
                setName(task.name || '');
                rootRef.current?.focus({ preventScroll: true });
              }
            }}
            placeholder={mode === 'new' ? 'What needs doing?' : 'Task name'}
            aria-label="Task name"
            className="-mx-1.5 block text-start w-[calc(100%+12px)] resize-none overflow-hidden rounded-lg border border-transparent bg-transparent px-1.5 py-0.5 font-heading text-[19px] font-extrabold leading-[1.25] tracking-[-0.01em] text-ink outline-none [field-sizing:content] placeholder:text-ink-4 hover:border-line focus:border-[color:var(--brand-soft-border)] focus:shadow-focus"
            data-testid="task-detail-title"
          />
        )}
        <SaveLine mode={mode} save={save} task={task} />
      </header>

      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-[18px] py-4" data-testid="task-pane-body">
        {readOnly ? (
          <div className="flex flex-col gap-1.5" data-testid="task-pane-read-only">
            <div className="flex items-center gap-2">
              <span className="text-[12.5px] text-ink-3">Status</span>
              <StatusPill status={status} domain="task" size="sm" data-testid="task-pane-status-read-only" />
            </div>
            {status === 'Blocked' ? (
              <p className="text-[12.5px] font-semibold text-status-red-ink">Blocked: {view.blocked_reason || 'no reason given yet'}</p>
            ) : null}
            <p className="text-[11.5px] text-ink-3">View only — your role can’t change tasks.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-1.5">
            <Segmented
              ariaLabel="Status"
              value={status}
              onChange={pickStatus}
              options={STATUS_SEGMENTS
                .filter((option) => mode === 'task' || option.key !== 'Done')
                .map((option) => ({ ...option, dot: option.key === status ? option.dot : undefined }))}
              fullWidth
              testId="task-detail-status"
            />
            {blockedDraft !== null ? (
              <div className="flex flex-col gap-2 rounded-[10px] border border-status-red-bg bg-status-red-bg px-3 py-2.5" data-testid="task-blocked-form">
                <label htmlFor="task-blocked-reason" className="text-[12.5px] font-bold text-status-red-ink">Why is it blocked?</label>
                <input
                  id="task-blocked-reason"
                  autoFocus
                  value={blockedDraft}
                  onChange={(event) => setBlockedDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      confirmBlocked();
                    }
                    if (event.key === 'Escape') {
                      event.preventDefault();
                      event.stopPropagation();
                      setBlockedDraft(null);
                    }
                  }}
                  placeholder="e.g. Waiting LPO approval from client"
                  className="h-8 rounded-lg border border-line bg-surface px-2.5 text-[13px] text-ink outline-none placeholder:text-ink-4 focus:border-[color:var(--brand-soft-border)] focus:shadow-focus"
                  data-testid="task-blocked-reason"
                />
                <div className="flex items-center justify-end gap-2">
                  <Button type="button" variant="ghost" size="xs" onClick={() => setBlockedDraft(null)}>Cancel</Button>
                  <Button type="button" size="xs" onClick={confirmBlocked} disabled={!String(blockedDraft).trim()} data-testid="task-blocked-save">
                    Set blocked
                  </Button>
                </div>
              </div>
            ) : status === 'Blocked' ? (
              <p className="flex items-start gap-1.5 text-[12.5px] font-semibold text-status-red-ink">
                <span className="min-w-0 flex-1">Blocked: {view.blocked_reason || 'no reason given yet'}</span>
                <button type="button" className={linkButton} onClick={() => setBlockedDraft(view.blocked_reason || '')}>Edit reason</button>
              </p>
            ) : status === 'Cancelled' ? (
              <p className="flex items-center gap-1.5 text-[12.5px] font-semibold text-ink-3">
                <CircleSlash className="h-3.5 w-3.5" aria-hidden="true" /> Cancelled
                <button type="button" className={cn(linkButton, 'ml-1')} onClick={() => onReopen(task)}>Reopen</button>
              </p>
            ) : (
              <p className="text-[11.5px] text-ink-3">Blocked asks for a reason{mode === 'task' ? ' · Cancel lives in ⋯' : ''}</p>
            )}
          </div>
        )}

        <dl className="grid grid-cols-[96px_minmax(0,1fr)] items-center gap-x-2 gap-y-2.5 text-[13.5px]">
          <dt className="text-[12.5px] text-ink-3">Priority</dt>
          <dd className="min-w-0">
            {readOnly ? readValue(<PriorityFlag priority={view.priority} className="text-[13.5px]" />, 'task-pane-priority-read-only') : (
              <Picker
                open={openPicker === 'priority'}
                onOpenChange={(open) => setOpenPicker(open ? 'priority' : null)}
                label={`Priority: ${view.priority || 'Medium'}`}
                testId="task-pane-priority"
                trigger={<PriorityFlag priority={view.priority} className="text-[13.5px]" />}
              >
                <PriorityPanel
                  value={view.priority || 'Medium'}
                  onPick={(priority) => {
                    setOpenPicker(null);
                    if (priority !== view.priority) change({ priority }, { message: mode === 'task' ? `“${task.name}” is ${priority} priority` : undefined });
                  }}
                />
              </Picker>
            )}
          </dd>

          <dt className="text-[12.5px] text-ink-3">{start ? 'Dates' : 'Due'}</dt>
          <dd className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
            {readOnly ? (
              <>
                {start ? <span className="text-[13.5px] font-semibold text-ink">{formatDate(start, { year: 'auto' })}</span> : null}
                {start ? <span className="text-ink-3" aria-hidden="true">→</span> : null}
                {readValue(dueText, 'task-pane-due-read-only')}
                {start ? <span className="text-[12px] font-medium text-ink-3">· {workingDaysBetween(start, due)} working days</span> : null}
              </>
            ) : (
              <>
                {start ? (
                  <Picker
                    open={openPicker === 'start'}
                    onOpenChange={(open) => setOpenPicker(open ? 'start' : null)}
                    label="Start date"
                    testId="task-pane-start"
                    trigger={<span>{formatDate(start, { year: 'auto' })}</span>}
                  >
                    <DueDatePanel
                      title="Start date"
                      value={start}
                      onPick={(value) => {
                        setOpenPicker(null);
                        if (!value) change({ is_multi_day: false });
                        else if (due && value > due) notify.error('The start must be on or before the end date');
                        else change({ is_multi_day: true, start_date: value });
                      }}
                    />
                  </Picker>
                ) : null}
                {start ? <span className="text-ink-3" aria-hidden="true">→</span> : null}
                <Picker
                  open={openPicker === 'due'}
                  onOpenChange={(open) => setOpenPicker(open ? 'due' : null)}
                  label={due ? `Due ${formatDate(due)}` : 'Set a due date'}
                  testId="task-pane-due"
                  trigger={dueText}
                >
                  <DueDatePanel
                    title={start ? 'End date' : 'Due date'}
                    value={due}
                    onPick={(value) => {
                      setOpenPicker(null);
                      if (value === due) return;
                      if (start && value && value < start) notify.error('The end must be on or after the start date');
                      else change(start && !value ? { due_date: '', is_multi_day: false } : { due_date: value });
                    }}
                  />
                </Picker>
                {start ? (
                  <>
                    <span className="text-[12px] font-medium text-ink-3">· {workingDaysBetween(start, due)} working days</span>
                    <button type="button" className={linkButton} onClick={() => change({ is_multi_day: false })}>Single day</button>
                  </>
                ) : (
                  <Popover open={openPicker === 'multi'} onOpenChange={(open) => setOpenPicker(open ? 'multi' : null)}>
                    <PopoverTrigger asChild>
                      <button type="button" className={linkButton} data-testid="task-pane-make-multi-day">Make multi-day</button>
                    </PopoverTrigger>
                    <PopoverContent align="start" sideOffset={6} collisionPadding={12} className={popoverClasses}>
                      <DueDatePanel
                        title="Starts on"
                        value=""
                        onPick={(value) => {
                          setOpenPicker(null);
                          if (!value) return;
                          if (due && value > due) notify.error('The start must be on or before the due date');
                          else change({ is_multi_day: true, start_date: value, due_date: due || value });
                        }}
                      />
                    </PopoverContent>
                  </Popover>
                )}
              </>
            )}
          </dd>

          <dt className="text-[12.5px] text-ink-3">Assignees</dt>
          <dd className="flex min-w-0 flex-wrap items-center gap-1.5" data-testid="task-detail-assignees">
            {shownAssignees.map((person) => (
              <span key={person.id} className="group inline-flex h-7 max-w-full items-center gap-1.5 rounded-full pr-1 text-[13.5px] font-semibold text-ink">
                <PersonAvatar person={person} />
                <span className="truncate">{person.name}</span>
                {readOnly ? null : (
                  <button
                    type="button"
                    onClick={() => toggleAssigneeId(person.id)}
                    className="grid h-5 w-5 place-items-center rounded-full text-ink-3 opacity-0 transition-opacity hover:bg-surface-hover hover:text-ink focus-visible:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100"
                    aria-label={`Remove ${person.name}`}
                  >
                    <X className="h-3 w-3" aria-hidden="true" />
                  </button>
                )}
              </span>
            ))}
            {!shownAssignees.length ? <span className="text-[13px] font-semibold text-status-amber-ink">Unassigned</span> : null}
            {readOnly ? null : (
              <Popover open={openPicker === 'people'} onOpenChange={(open) => setOpenPicker(open ? 'people' : null)}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex h-7 items-center gap-1 rounded-full border border-dashed border-[color:var(--line-dashed)] px-2.5 text-[12.5px] font-semibold text-ink-2 transition-colors hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    data-testid="task-detail-add-person"
                  >
                    <Plus className="h-3.5 w-3.5" aria-hidden="true" /> Add
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" sideOffset={6} collisionPadding={12} className={popoverClasses}>
                  <AssignPanel title="Assignees" members={members} selectedIds={assigneeIds} onToggle={(member) => toggleAssigneeId(member.id)} onAdd={addMember} />
                </PopoverContent>
              </Popover>
            )}
          </dd>

          <dt className="text-[12.5px] text-ink-3">Project</dt>
          <dd className="flex min-w-0 flex-wrap items-center gap-1.5">
            {shownProjects.map((item) => <span key={item.id} className={cn(tagClasses, 'h-auto min-h-6 whitespace-normal break-words')} title={projectLabel(item)}>{projectLabel(item)}</span>)}
            {readOnly ? (shownProjects.length ? null : <span className="text-[13px] font-medium text-ink-3">No project</span>) : (
              <Popover open={openPicker === 'project'} onOpenChange={(open) => setOpenPicker(open ? 'project' : null)}>
                <PopoverTrigger asChild>
                  <button type="button" className={cn(shownProjects.length ? linkButton : 'inline-flex h-6 items-center gap-1 rounded-md border border-dashed border-[color:var(--line-dashed)] px-2 text-[12.5px] font-semibold text-ink-2 hover:bg-surface-hover')} data-testid="task-pane-project">
                    {shownProjects.length ? 'Change' : <><Plus className="h-3.5 w-3.5" aria-hidden="true" /> Add project</>}
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" sideOffset={6} collisionPadding={12} className={popoverClasses}>
                  <ProjectPanel
                    projects={projects}
                    selectedIds={projectIds}
                    lockedIds={homeProjectId ? [homeProjectId] : []}
                    hint={homeProjectId ? `It came from ${origin.kind === 'invoicing' ? 'an invoice' : origin.kind === 'stage' ? 'a stage' : 'the material list'} in its project, so it stays there — you can add others.` : ''}
                    onToggle={(item) => toggleProjectId(item.id)}
                  />
                </PopoverContent>
              </Popover>
            )}
          </dd>

          <dt className="text-[12.5px] text-ink-3">Section</dt>
          <dd className="min-w-0">
            {readOnly ? readValue(<span className={cn('truncate', !section && 'font-medium text-ink-3')}>{section || NO_SECTION}</span>, 'task-pane-section-read-only') : (
              <Picker
                open={openPicker === 'section'}
                onOpenChange={(open) => setOpenPicker(open ? 'section' : null)}
                label={`Section: ${section || NO_SECTION}`}
                testId="task-pane-section"
                trigger={<><span className={cn('truncate', !section && 'font-medium text-ink-3')}>{section || NO_SECTION}</span><ChevronDown className="h-3.5 w-3.5 shrink-0 text-ink-4" aria-hidden="true" /></>}
              >
                <MovePanel
                  title="Section"
                  sections={options.sections || []}
                  current={section || NO_SECTION}
                  onPick={(next) => {
                    setOpenPicker(null);
                    const value = next === NO_SECTION ? '' : next;
                    if (mode === 'new') setForm((current) => ({ ...current, section: value }));
                    else moveSection(next);
                  }}
                  onAdd={async (value) => {
                    const added = await addOption('section', value);
                    if (!added) return;
                    setOpenPicker(null);
                    if (mode === 'new') setForm((current) => ({ ...current, section: added }));
                    else moveSection(added);
                  }}
                />
              </Picker>
            )}
          </dd>

          <dt className="text-[12.5px] text-ink-3">Location</dt>
          <dd className="flex min-w-0 flex-wrap items-center gap-1.5">
            {locations.map((location) => <span key={location} className={tagClasses}>{location}</span>)}
            {readOnly ? (locations.length ? null : <span className="text-[13px] font-medium text-ink-3">None</span>) : (
              <Popover open={openPicker === 'location'} onOpenChange={(open) => setOpenPicker(open ? 'location' : null)}>
                <PopoverTrigger asChild>
                  <button type="button" className={cn(locations.length ? linkButton : 'inline-flex h-6 items-center gap-1 rounded-md border border-dashed border-[color:var(--line-dashed)] px-2 text-[12.5px] font-semibold text-ink-2 hover:bg-surface-hover')} data-testid="task-pane-location">
                    {locations.length ? 'Change' : <><Plus className="h-3.5 w-3.5" aria-hidden="true" /> Add</>}
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" sideOffset={6} collisionPadding={12} className={popoverClasses}>
                  <LocationPanel
                    locations={options.locations || []}
                    selected={locations}
                    onToggle={toggleLocation}
                    onAdd={async (value) => {
                      const added = await addOption('location', value);
                      if (added) await toggleLocation(added);
                    }}
                  />
                </PopoverContent>
              </Popover>
            )}
          </dd>

          {origin ? (
            <>
              <dt className="self-start pt-0.5 text-[12.5px] text-ink-3">Origin</dt>
              <dd className="flex min-w-0 flex-col items-start gap-0.5" data-testid="task-detail-origin">
                <span className="inline-flex min-w-0 max-w-full items-center gap-1.5 font-semibold text-ink" title={origin.title}>
                  <OriginIcon className="h-3.5 w-3.5 shrink-0 text-ink-3" aria-hidden="true" />
                  <span className="truncate">{origin.label}</span>
                </span>
                {origin.href ? (
                  <Link to={origin.href} className={cn(linkButton, 'inline-flex items-center gap-1')} data-testid="task-detail-origin-link">
                    <ExternalLink className="h-3 w-3" aria-hidden="true" /> Open in project
                  </Link>
                ) : null}
              </dd>
            </>
          ) : null}
        </dl>

        <section className="flex flex-col gap-1.5" aria-label="Notes">
          <div className={sectionLabel}>Notes</div>
          {mode === 'new' ? (
            <NotesBlock html={form.notes_html || ''} onDraft={(html) => setForm((current) => ({ ...current, notes_html: html }))} />
          ) : (
            <NotesBlock
              html={view.notes_html || ''}
              disabled={readOnly}
              onDirtyChange={setNotesDirty}
              onSave={(html) => change({ notes_html: html, description: notesToText(html).slice(0, 500) }, { message: 'Notes saved' })}
            />
          )}
        </section>

        <Attachments
          items={mode === 'task' ? (task.attachments || []) : []}
          pending={pendingFiles}
          uploading={uploading}
          onFiles={(files) => {
            if (mode === 'new') setPendingFiles((current) => [...current, ...files]);
            else upload(files);
          }}
          onRemove={removeAttachment}
          onRemovePending={(file) => setPendingFiles((current) => current.filter((item) => item !== file))}
          disabled={readOnly}
        />

        {mode === 'task' ? (
          <Comments task={task} user={user} onPost={postComment} onDelete={deleteComment} onDirtyChange={setCommentDirty} readOnly={readOnly} />
        ) : null}
      </div>

      <footer className="flex flex-wrap items-center gap-2 border-t border-line-2 px-[18px] py-3">
        {mode === 'new' ? (
          <>
            <Button type="button" onClick={create} disabled={!name.trim() || creating} data-testid="task-create-submit">
              {creating ? <Loader2 className="animate-spin" aria-hidden="true" /> : <Plus aria-hidden="true" />} Create task
            </Button>
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <span className="flex-1" />
            <span className="text-[12px] text-ink-3 max-sm:hidden">Enter to create · Esc to close</span>
          </>
        ) : (
          <>
            {readOnly ? (
              <span className="text-[12px] font-semibold text-ink-3" data-testid="task-pane-view-only">View only</span>
            ) : status === 'Done' ? (
              <Button type="button" variant="outline" onClick={() => onReopen(task)} data-testid="task-detail-reopen">
                <RotateCcw aria-hidden="true" /> Reopen
              </Button>
            ) : (
              <Button type="button" variant="outline" onClick={() => onDone(task)} data-testid="task-detail-done">
                <CircleCheck className="text-status-green" aria-hidden="true" /> Mark done
              </Button>
            )}
            {nextUp ? (
              <Button type="button" variant="ghost" onClick={nextUp.onOpen} title={`Next: ${nextUp.label}`} data-testid="task-pane-next">
                Next: <span className="max-w-[120px] truncate">{nextUp.label}</span> ›
              </Button>
            ) : null}
            <span className="flex-1" />
            <span className="text-[12px] text-ink-3 max-sm:hidden">Esc to close</span>
          </>
        )}
      </footer>
    </>
  );

  if (docked) {
    return (
      <aside
        ref={rootRef}
        tabIndex={-1}
        aria-label={mode === 'new' ? 'New task' : `Task: ${task.name}`}
        // Focus lands here when a task opens (a click, or a /tasks?task=… link): no ring around the whole pane —
        // the controls inside keep their own rings.
        className="sticky top-[calc(var(--app-header-height,64px)+16px)] flex max-h-[calc(100vh-var(--app-header-height,64px)-32px)] min-w-0 flex-col overflow-hidden rounded-card border border-line bg-surface text-ink shadow-raised outline-none"
        data-testid="task-detail-sheet"
        data-task-id={mode === 'task' ? task.id : 'new'}
      >
        {content}
      </aside>
    );
  }
  return (
    <Sheet open onOpenChange={(open) => { if (!open) onClose(); }}>
      <SheetContent
        side="right"
        hideClose
        overlayClassName="bg-[color:var(--scrim)]"
        className="flex w-full flex-col gap-0 border-l border-line bg-surface p-0 text-ink shadow-overlay outline-none focus:outline-none focus-visible:outline-none sm:max-w-[440px]"
        data-testid="task-detail-sheet"
        data-task-id={mode === 'task' ? task.id : 'new'}
        onOpenAutoFocus={(event) => { if (mode === 'task') event.preventDefault(); }}
        // Never let the dialog close itself on Escape: the pane's Escape rules decide (a box inside closes
        // first, then the pane — asking while something is unsaved), exactly as in the docked pane.
        onEscapeKeyDown={(event) => {
          event.preventDefault();
          sheetEscapeRef.current = event;
        }}
      >
        <SheetTitle className="sr-only">{mode === 'new' ? 'New task' : task.name}</SheetTitle>
        <SheetDescription className="sr-only">Read and change the task; every change saves on its own.</SheetDescription>
        <div ref={rootRef} tabIndex={-1} className="flex min-h-0 flex-1 flex-col outline-none">{content}</div>
      </SheetContent>
    </Sheet>
  );
}

export function copyLink(task) {
  const url = `${window.location.origin}/tasks?task=${encodeURIComponent(task.id)}`;
  const done = () => notify.success('Link copied', { description: url });
  try {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(done, () => notify.info('Copy this link', { description: url }));
      return;
    }
  } catch {
    // fall through
  }
  notify.info('Copy this link', { description: url });
}
